import {
  ArrowForwardOutlined,
  AutoAwesomeOutlined,
  CheckCircleOutlined,
  DescriptionOutlined,
  FolderOutlined,
  GroupsOutlined,
  KeyboardArrowDownOutlined,
  ShieldOutlined,
  TaskAltOutlined,
} from '@mui/icons-material';
import { Box, Button, Chip, Container, Stack, Typography } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import React from 'react';

const MotionBox = motion.create(Box);

const sourceItems = [
  { icon: <FolderOutlined />, value: '12', label: '项目资料' },
  { icon: <DescriptionOutlined />, value: '1', label: '会议记录' },
  { icon: <GroupsOutlined />, value: '3', label: '企业制度' },
];

const agentSteps = [
  {
    icon: <CheckCircleOutlined />,
    title: '理解业务上下文',
    detail: '关联项目、会议、文档与流程数据',
  },
  {
    icon: <AutoAwesomeOutlined />,
    title: '生成风险与行动项',
    detail: '识别 4 项风险，整理 7 个待办',
  },
  {
    icon: <TaskAltOutlined />,
    title: '提交人工确认',
    detail: '关键动作经负责人确认后进入执行',
  },
];

interface HeroProps {
  onContact: () => void;
}

const Hero: React.FC<HeroProps> = ({ onContact }) => {
  const reduceMotion = useReducedMotion();
  const enter = (delay = 0) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.62, delay },
        };

  return (
    <Box
      id="top"
      component="section"
      sx={{
        pt: { xs: 12, md: 15 },
        pb: { xs: 10, md: 13 },
        position: 'relative',
        overflow: 'hidden',
        background:
          'linear-gradient(145deg, rgb(var(--hero-from-rgb)) 0%, rgb(var(--bg-rgb)) 58%, rgb(var(--surface-rgb)) 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          opacity: 0.36,
          backgroundImage:
            'linear-gradient(rgba(var(--brand-rgb), .09) 1px, transparent 1px), linear-gradient(90deg, rgba(var(--brand-rgb), .09) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
          maskImage:
            'linear-gradient(to bottom, black 0%, transparent 76%), linear-gradient(to right, transparent 0%, black 35%, black 82%, transparent 100%)',
          maskComposite: 'intersect',
          WebkitMaskComposite: 'source-in',
        },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          width: 520,
          height: 520,
          borderRadius: '50%',
          top: -290,
          right: -180,
          background: 'rgba(var(--accent-rgb), 0.10)',
          filter: 'blur(4px)',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(460px, .92fr)' },
            alignItems: 'center',
            gap: { xs: 7, lg: 8 },
          }}
        >
          <Box>
            <MotionBox {...enter()}>
              <Chip
                icon={<AutoAwesomeOutlined />}
                label="飞舟 Agent 办公"
                sx={{
                  mb: 3,
                  height: 34,
                  borderRadius: '999px',
                  backgroundColor: 'rgba(var(--brand-tint-rgb), 0.10)',
                  color: 'rgb(var(--brand-rgb))',
                  border: '1px solid rgba(var(--brand-tint-rgb), 0.16)',
                  '& .MuiChip-icon': { color: 'inherit', fontSize: 18 },
                }}
              />
              <Typography
                component="h1"
                sx={{
                  maxWidth: 760,
                  fontSize: { xs: '2.55rem', sm: '3.35rem', md: '4.5rem' },
                  fontWeight: 800,
                  letterSpacing: '-0.055em',
                  lineHeight: { xs: 1.12, md: 1.05 },
                  color: 'rgb(var(--ink-rgb))',
                }}
              >
                把日常办公，
                <Box component="span" sx={{ display: 'block', color: 'rgb(var(--brand-rgb))' }}>
                  交给会做事的 Agent。
                </Box>
              </Typography>
            </MotionBox>

            <MotionBox {...enter(0.08)}>
              <Typography
                sx={{
                  mt: 3,
                  maxWidth: 650,
                  color: 'rgba(var(--ink-rgb), 0.68)',
                  fontSize: { xs: '1.02rem', md: '1.14rem' },
                  lineHeight: 1.85,
                }}
              >
                飞舟面向工程企业，把会议、文档、项目资料与审批待办汇聚到统一业务上下文。
                Agent 理解目标、拆解任务、调用办公工具，并在关键节点交由人确认。
              </Typography>
            </MotionBox>

            <MotionBox {...enter(0.15)}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4.5 }}>
                <Button
                  onClick={onContact}
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardOutlined />}
                  sx={{
                    minHeight: 50,
                    px: 3.2,
                    borderRadius: '999px',
                    backgroundColor: 'rgb(var(--ink-rgb))',
                    color: 'rgb(var(--surface-rgb))',
                    boxShadow: '0 12px 30px rgba(var(--shadow-rgb), 0.16)',
                    '&:hover': {
                      backgroundColor: 'rgb(var(--brand-rgb))',
                      boxShadow: '0 12px 30px rgba(var(--shadow-rgb), 0.2)',
                    },
                  }}
                >
                  预约产品交流
                </Button>
                <Button
                  component="a"
                  href="#workflow"
                  size="large"
                  endIcon={<KeyboardArrowDownOutlined />}
                  sx={{
                    minHeight: 50,
                    px: 3,
                    borderRadius: '999px',
                    color: 'rgb(var(--ink-rgb))',
                    border: '1px solid rgba(var(--ink-rgb), 0.18)',
                    '&:hover': {
                      borderColor: 'rgba(var(--brand-rgb), 0.55)',
                      backgroundColor: 'rgba(var(--brand-rgb), 0.06)',
                    },
                  }}
                >
                  看看如何工作
                </Button>
              </Stack>
            </MotionBox>

            <MotionBox {...enter(0.22)}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 2.4 }}
                sx={{
                  mt: 4.5,
                  color: 'rgba(var(--ink-rgb), 0.58)',
                  fontSize: '0.86rem',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <CheckCircleOutlined sx={{ fontSize: 17, color: 'rgb(var(--brand-rgb))' }} />
                  理解企业上下文
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <CheckCircleOutlined sx={{ fontSize: 17, color: 'rgb(var(--brand-rgb))' }} />
                  调用办公工具
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <CheckCircleOutlined sx={{ fontSize: 17, color: 'rgb(var(--brand-rgb))' }} />
                  结果可追溯
                </Box>
              </Stack>
            </MotionBox>
          </Box>

          <MotionBox
            {...(reduceMotion
              ? {}
              : {
                  initial: { opacity: 0, y: 32, rotate: 1.5 },
                  animate: { opacity: 1, y: 0, rotate: 0 },
                  transition: { duration: 0.72, delay: 0.16 },
                })}
            sx={{ position: 'relative', minWidth: 0 }}
          >
            <Box
              sx={{
                position: 'relative',
                borderRadius: '28px',
                p: { xs: 2, sm: 2.5 },
                backgroundColor: 'rgba(var(--surface-rgb), 0.88)',
                border: '1px solid rgba(var(--line-rgb), 0.11)',
                boxShadow: '0 32px 80px rgba(var(--shadow-rgb), 0.17)',
                backdropFilter: 'blur(20px)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pb: 1.8,
                  borderBottom: '1px solid rgba(var(--line-rgb), 0.09)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: '11px',
                      display: 'grid',
                      placeItems: 'center',
                      color: 'rgb(var(--on-brand-rgb))',
                      backgroundColor: 'rgb(var(--brand-rgb))',
                    }}
                  >
                    <AutoAwesomeOutlined sx={{ fontSize: 19 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
                      项目周会整理
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: 'rgba(var(--ink-rgb), 0.48)' }}>
                      Agent 正在推进 · 09:20
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label="待确认 2 项"
                  size="small"
                  sx={{
                    color: 'rgb(var(--accent-rgb))',
                    backgroundColor: 'rgba(var(--accent-rgb), 0.10)',
                    fontWeight: 650,
                  }}
                />
              </Box>

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: '18px',
                  backgroundColor: 'rgba(var(--brand-rgb), 0.065)',
                  border: '1px solid rgba(var(--brand-rgb), 0.13)',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: '0.66rem',
                    letterSpacing: '0.12em',
                    color: 'rgb(var(--brand-rgb))',
                    mb: 1,
                  }}
                >
                  TASK BRIEF
                </Typography>
                <Typography sx={{ fontWeight: 650, lineHeight: 1.65, fontSize: '0.96rem' }}>
                  根据本周项目例会和资料，整理风险、待办与下周跟进计划。
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 1,
                  mt: 1.5,
                }}
              >
                {sourceItems.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      borderRadius: '14px',
                      p: 1.25,
                      border: '1px solid rgba(var(--line-rgb), 0.08)',
                      backgroundColor: 'rgba(var(--surface-soft-rgb), 0.72)',
                    }}
                  >
                    <Box sx={{ color: 'rgb(var(--brand-rgb))', display: 'flex', fontSize: 18 }}>
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 750, mt: 0.7, fontSize: '0.92rem' }}>
                      {item.value}
                    </Typography>
                    <Typography sx={{ color: 'rgba(var(--ink-rgb), 0.5)', fontSize: '0.67rem' }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Stack spacing={0} sx={{ mt: 2 }}>
                {agentSteps.map((step, index) => (
                  <Box
                    key={step.title}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      gap: 1.4,
                      pb: index === agentSteps.length - 1 ? 0 : 1.7,
                    }}
                  >
                    {index < agentSteps.length - 1 && (
                      <Box
                        aria-hidden="true"
                        sx={{
                          position: 'absolute',
                          left: 16,
                          top: 34,
                          bottom: 3,
                          width: 1,
                          backgroundColor: 'rgba(var(--brand-rgb), 0.2)',
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        zIndex: 1,
                        width: 33,
                        height: 33,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                        color: 'rgb(var(--brand-rgb))',
                        backgroundColor: 'rgb(var(--surface-rgb))',
                        border: '1px solid rgba(var(--brand-rgb), 0.25)',
                      }}
                    >
                      {step.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.86rem' }}>
                        {step.title}
                      </Typography>
                      <Typography sx={{ color: 'rgba(var(--ink-rgb), 0.54)', fontSize: '0.75rem', mt: 0.25 }}>
                        {step.detail}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>

              <Box
                sx={{
                  mt: 2,
                  pt: 1.7,
                  borderTop: '1px solid rgba(var(--line-rgb), 0.09)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'rgba(var(--ink-rgb), 0.62)',
                  fontSize: '0.75rem',
                }}
              >
                <ShieldOutlined sx={{ fontSize: 18, color: 'rgb(var(--brand-rgb))' }} />
                高风险操作等待人工确认，完整记录执行依据
              </Box>
            </Box>

            <Box
              sx={{
                position: 'absolute',
                left: { xs: -10, sm: -28 },
                bottom: { xs: -26, sm: -34 },
                px: 1.6,
                py: 1.2,
                borderRadius: '14px',
                backgroundColor: 'rgb(var(--accent-rgb))',
                color: 'white',
                boxShadow: '0 14px 32px rgba(var(--shadow-rgb), 0.22)',
                transform: reduceMotion ? 'none' : 'rotate(-3deg)',
              }}
            >
              <Typography sx={{ fontSize: '0.66rem', letterSpacing: '0.08em', opacity: 0.8 }}>
                HUMAN IN THE LOOP
              </Typography>
              <Typography sx={{ fontWeight: 750, fontSize: '0.86rem' }}>
                关键决定始终由人确认
              </Typography>
            </Box>
          </MotionBox>
        </Box>
      </Container>
    </Box>
  );
};

export default Hero;
